import type { ComponentType } from 'react';
import type { ViewType } from '../config/permissions';
import {
  AlertsTabIcon,
  AudioTabIcon,
  CalendarTabIcon,
  ExplorerTabIcon,
  MarketTabIcon,
} from './MobileTabIcons';
import { MobileAlertsTab } from './tabs/MobileAlertsTab';
import { MobileAudioTab } from './tabs/MobileAudioTab';
import { MobileCalendarTab } from './tabs/MobileCalendarTab';
import { MobileExplorerTab } from './tabs/MobileExplorerTab';
import { MobileMarketTab } from './tabs/MobileMarketTab';

/**
 * Root bottom-bar destinations only (product IA r2).
 * Nested explorer tools / Audio WSJ hub are not bottom-tab ids.
 */
export type MobileTabId =
  | 'alerts'
  | 'explorer'
  | 'market'
  | 'calendar'
  | 'audio';

export interface MobileTabDef {
  id: MobileTabId;
  label: string;
  /** Monochrome SVG; inherits tab color via currentColor. */
  icon: ComponentType<{ size?: number; className?: string }>;
  enabled: boolean;
  component: ComponentType;
  /**
   * Shared-route path for deep links / tab switches.
   * Alerts uses `/` on mobile (desktop `/` is dashboard; shell swaps chrome).
   */
  path: string;
  /**
   * When true, main content is a flex fill (no vertical shell scroll).
   */
  fillContent?: boolean;
  /**
   * Tier-gated destination. The mobile shell renders tab components directly
   * rather than through the router, so the `ViewGate` wrappers on the desktop
   * routes do not cover this path — `MobileWorkspace` applies the gate itself.
   */
  viewType?: ViewType;
}

/**
 * Single source of truth for mobile bottom tabs.
 * MobileTabBar + MobileWorkspace only map this list.
 */
export const MOBILE_TABS: readonly MobileTabDef[] = [
  {
    id: 'alerts',
    label: 'Alerts',
    icon: AlertsTabIcon,
    enabled: true,
    component: MobileAlertsTab,
    path: '/',
    fillContent: true,
  },
  {
    id: 'explorer',
    viewType: 'ticker-explorer',
    label: 'Explorer',
    icon: ExplorerTabIcon,
    enabled: true,
    component: MobileExplorerTab,
    path: '/explorer',
    fillContent: true,
  },
  {
    id: 'market',
    viewType: 'market-overview',
    label: 'Market',
    icon: MarketTabIcon,
    enabled: true,
    component: MobileMarketTab,
    path: '/market',
    fillContent: true,
  },
  {
    id: 'calendar',
    viewType: 'calendars',
    label: 'Calendar',
    icon: CalendarTabIcon,
    enabled: true,
    component: MobileCalendarTab,
    path: '/calendar',
    fillContent: true,
  },
  {
    id: 'audio',
    label: 'Audio',
    icon: AudioTabIcon,
    enabled: true,
    component: MobileAudioTab,
    path: '/audio',
    fillContent: true,
  },
];

export function mobileTabById(id: MobileTabId): MobileTabDef {
  const tab = MOBILE_TABS.find((t) => t.id === id);
  if (!tab) throw new Error(`Unknown mobile tab: ${id}`);
  return tab;
}

/** Map a pathname to a tab id (deep links). Default: alerts. */
export function tabIdFromPathname(pathname: string): MobileTabId {
  if (pathname === '/audio' || pathname.startsWith('/audio/')) return 'audio';
  if (pathname === '/calendar' || pathname.startsWith('/calendar/')) return 'calendar';
  if (pathname === '/market' || pathname.startsWith('/market/')) return 'market';
  if (
    pathname === '/explorer' ||
    pathname.startsWith('/explorer/') ||
    pathname.startsWith('/ticker/')
  ) {
    return 'explorer';
  }
  // /, /profile, /courses, unknown → default landing Alerts (eng default)
  return 'alerts';
}
